import { useState } from "react";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const schema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320),
  organization: z.string().trim().max(200).optional().or(z.literal("")),
  project_summary: z.string().trim().min(10).max(4000),
  budget_range: z.string().trim().max(100).optional().or(z.literal("")),
  timeline: z.string().trim().max(100).optional().or(z.literal("")),
});

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  affiliateId: string;
  affiliateName: string;
}

const RequestQuoteDialog = ({ open, onOpenChange, affiliateId, affiliateName }: Props) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    project_summary: "",
    budget_range: "",
    timeline: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please fill required fields");
      return;
    }
    setLoading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("affiliate_leads").insert({
        affiliate_id: affiliateId,
        requester_user_id: userData.user?.id ?? null,
        name: parsed.data.name,
        email: parsed.data.email,
        project_summary: parsed.data.project_summary,
        organization: parsed.data.organization || null,
        budget_range: parsed.data.budget_range || null,
        timeline: parsed.data.timeline || null,
      });
      if (error) throw error;
      toast.success("Request sent. The provider will reach out shortly.");
      onOpenChange(false);
      setForm({
        name: "",
        email: "",
        organization: "",
        project_summary: "",
        budget_range: "",
        timeline: "",
      });
    } catch (e: any) {
      toast.error(e.message ?? "Could not send request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Request a quote from {affiliateName}</DialogTitle>
          <DialogDescription>
            Share a few details and the provider will respond directly. Your
            name, email, organization, and project details will be shared with{" "}
            {affiliateName} so they can follow up. We don't share them with
            anyone else.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Your name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1"
                maxLength={200}
              />
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1"
                maxLength={320}
              />
            </div>
          </div>
          <div>
            <Label className="text-sm">Organization (optional)</Label>
            <Input
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              className="mt-1"
              maxLength={200}
            />
          </div>
          <div>
            <Label className="text-sm">Project summary</Label>
            <Textarea
              value={form.project_summary}
              onChange={(e) => setForm({ ...form, project_summary: e.target.value })}
              className="mt-1"
              rows={5}
              maxLength={4000}
              placeholder="What do you need help with? Scope, deliverables, context."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Budget range (optional)</Label>
              <Input
                value={form.budget_range}
                onChange={(e) => setForm({ ...form, budget_range: e.target.value })}
                className="mt-1"
                maxLength={100}
              />
            </div>
            <div>
              <Label className="text-sm">Timeline (optional)</Label>
              <Input
                value={form.timeline}
                onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                className="mt-1"
                maxLength={100}
                placeholder="e.g. 4-6 weeks"
              />
            </div>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full bg-primary text-primary-foreground">
            {loading ? "Sending..." : "Send Request"}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            By submitting, you agree to share your contact details with{" "}
            {affiliateName} for the purpose of responding to this request.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestQuoteDialog;